import type { FC, ReactNode } from 'react'
import type { SellerVerification } from '@/lib/marketplace/seller-verification'
import type { SellerStat } from '@/components/marketplace/seller/seller-stats'

import { useEffect, useState } from 'react'
import { BadgeCheck, Check, Globe, MapPin, Share2 } from 'lucide-react'

import { cn } from '@/lib/utils'
import { TelegramGlyph } from '@/components/marketplace/telegram-glyph'
import { InstagramGlyph } from '@/components/marketplace/instagram-glyph'
import { SellerStats } from '@/components/marketplace/seller/seller-stats'
import { SellerVerificationBadges } from '@/components/marketplace/seller/seller-verification-badges'

const OUTLINE_BUTTON =
    'type-label mta-border mta-focus mta-scroll-safe inline-flex h-12 items-center justify-center gap-2 rounded-mta-sm bg-paper px-5 text-[11px] text-ink-900 transition-colors duration-[var(--dur-fast)] ease-[var(--ease-brand)] hover:bg-ink-900 hover:text-paper'

interface ShareProfileButtonProps {
    /** Absolute profile URL, resolved on the server. Empty string disables sharing. */
    url: string
    title: string
}

/**
 * Share control. The Web Share sheet is used where the browser has one and the
 * clipboard is the fallback, so the button always does something. Support is
 * read AFTER mount, never during render, so the server and the first client
 * paint agree.
 */
const ShareProfileButton: FC<ShareProfileButtonProps> = ({ url, title }): ReactNode => {
    const [copied, setCopied] = useState(false)
    const [canShare, setCanShare] = useState(false)

    useEffect(() => {
        setCanShare(typeof navigator !== 'undefined' && typeof navigator.share === 'function')
    }, [])

    useEffect(() => {
        if (!copied) return

        const timer = window.setTimeout(() => setCopied(false), 2200)

        return () => window.clearTimeout(timer)
    }, [copied])

    const share = async (): Promise<void> => {
        if (!url) return

        if (canShare) {
            try {
                await navigator.share({ title, url })

                return
            } catch {
                /* The visitor dismissed the sheet. Fall through to the copy path. */
            }
        }

        try {
            await navigator.clipboard.writeText(url)
            setCopied(true)
        } catch {
            setCopied(false)
        }
    }

    return (
        <button type='button' onClick={share} className={cn(OUTLINE_BUTTON, 'w-full sm:w-auto lg:w-full')}>
            {copied ? <Check className='size-4' aria-hidden='true' /> : <Share2 className='size-4' aria-hidden='true' />}
            {copied ? 'Link copied' : 'Share profile'}

            <span aria-live='polite' className='sr-only'>
                {copied ? 'Profile link copied to the clipboard.' : ''}
            </span>
        </button>
    )
}

interface SellerProfileHeaderProps {
    name: string
    username: string | null
    avatarUrl: string | null
    bio: string | null
    isBusiness: boolean
    restricted: boolean
    verifications: Array<SellerVerification>
    /** The one server-side verification answer. Never re-derived here. */
    verified: boolean
    /** Hover and screen-reader text for the badge, e.g. "2 of 4 checks completed". */
    verifiedTooltip: string
    stats: Array<SellerStat>
    /** Telegram deep link, already built from the seller's own handle. */
    telegramHref: string | null
    profileUrl: string
    onTelegramClick?: () => void
    /**
     * Self-declared profile details. Absent unless the seller filled them in,
     * and rendered as exactly that: where they say they are and where they say
     * they can be found. None of it is a verification, so none of it is styled
     * like one.
     */
    country?: string | null
    websiteUrl?: string | null
    instagramHandle?: string | null
}

/**
 * The seller profile header: a paper plate on the MTA canvas, black hairline,
 * poster name, the four verification checks, the derived stat strip, and the
 * two actions. Deliberately NOT a dark island, so the profile sits in the same
 * system as the rest of the marketplace.
 */
export const SellerProfileHeader: FC<SellerProfileHeaderProps> = ({
    name,
    username,
    avatarUrl,
    bio,
    isBusiness,
    restricted,
    verifications,
    verified,
    verifiedTooltip,
    stats,
    telegramHref,
    profileUrl,
    onTelegramClick,
    country,
    websiteUrl,
    instagramHandle
}): ReactNode => {
    const initial = name.trim().charAt(0).toUpperCase() || 'M'

    /*
        The website label drops the scheme so it reads as a place rather than a
        URL. The href keeps the full, normalised value the server stored, which
        is always http(s) — `normalizeWebsite` refuses anything else, so nothing
        needs sanitising at render time.
    */
    const websiteLabel = websiteUrl ? websiteUrl.replace(/^https?:\/\//, '').replace(/\/$/, '') : null
    const hasMeta = Boolean(country || websiteUrl || instagramHandle)

    return (
        <div className='mta-border rounded-mta-lg bg-paper p-4 shadow-[var(--offset-block-sm)] sm:p-7 lg:p-9'>
            <div className='flex flex-col gap-7 lg:flex-row lg:items-start lg:justify-between lg:gap-10'>
                <div className='min-w-0 flex-1'>
                    <div className='flex items-center gap-4 sm:items-start sm:gap-7'>
                        <div className='relative shrink-0'>
                            {avatarUrl ? (
                                // eslint-disable-next-line @next/next/no-img-element
                                <img
                                    src={avatarUrl}
                                    alt={`${name} profile photo`}
                                    width={160}
                                    height={160}
                                    decoding='async'
                                    className='mta-border size-20 rounded-mta-md object-cover sm:size-28'
                                />
                            ) : (
                                <span
                                    aria-hidden='true'
                                    className='type-display mta-border flex size-20 items-center justify-center rounded-mta-md bg-ink-900 text-[2rem] text-paper sm:size-28 sm:text-4xl'
                                >
                                    {initial}
                                </span>
                            )}

                            <span
                                tabIndex={0}
                                role='img'
                                aria-label={verifiedTooltip}
                                title={verifiedTooltip}
                                className={cn(
                                    'mta-focus absolute -bottom-1.5 -right-1.5 flex size-8 items-center justify-center rounded-full border-[3px] border-paper sm:size-9',
                                    verified ? 'bg-verify-blue text-white' : 'bg-surface-muted text-text-muted'
                                )}
                            >
                                <BadgeCheck className='size-4' aria-hidden='true' />
                            </span>
                        </div>

                        <div className='min-w-0 flex-1'>
                            <div className='flex flex-wrap items-center gap-x-3 gap-y-2'>
                                <h1 className='type-display text-[1.65rem] leading-none text-ink-900 sm:text-[2.6rem]'>
                                    {name}
                                </h1>

                                {isBusiness && (
                                    <span className='type-label mta-border rounded-mta-xs bg-surface-muted px-2 py-1 text-[10px] leading-none text-ink-900'>
                                        Business
                                    </span>
                                )}

                                {restricted && (
                                    <span className='type-label mta-border rounded-mta-xs bg-brand-red px-2 py-1 text-[10px] leading-none text-ink-900'>
                                        Restricted
                                    </span>
                                )}
                            </div>

                            {username && (
                                <p className='mt-2 text-sm font-medium text-text-muted'>
                                    {`@${username}`}
                                </p>
                            )}
                        </div>
                    </div>

                    {bio && (
                        <p className='mt-5 max-w-xl text-sm leading-relaxed text-text-muted'>
                            {bio}
                        </p>
                    )}

                    {hasMeta && (
                        <ul className='mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-text-muted'>
                            {country && (
                                <li className='flex items-center gap-2'>
                                    <MapPin className='size-4 shrink-0 text-ink-900/45' aria-hidden='true' />
                                    <span>{country}</span>
                                </li>
                            )}

                            {websiteUrl && websiteLabel && (
                                <li>
                                    <a
                                        href={websiteUrl}
                                        target='_blank'
                                        /* `ugc nofollow` because this link is
                                           the seller's own, not an endorsement,
                                           and must not pass ranking signal. */
                                        rel='noopener noreferrer nofollow ugc'
                                        className='mta-focus mta-scroll-safe inline-flex min-h-11 items-center gap-2 rounded-mta-xs underline-offset-4 hover:text-ink-900 hover:underline'
                                    >
                                        <Globe className='size-4 shrink-0 text-ink-900/45' aria-hidden='true' />
                                        {websiteLabel}
                                    </a>
                                </li>
                            )}

                            {instagramHandle && (
                                <li>
                                    <a
                                        href={`https://instagram.com/${instagramHandle}`}
                                        target='_blank'
                                        rel='noopener noreferrer nofollow ugc'
                                        className='mta-focus mta-scroll-safe inline-flex min-h-11 items-center gap-2 rounded-mta-xs underline-offset-4 hover:text-ink-900 hover:underline'
                                    >
                                        <InstagramGlyph className='size-4 shrink-0 text-ink-900/45' />
                                        {`@${instagramHandle}`}
                                    </a>
                                </li>
                            )}
                        </ul>
                    )}

                    <SellerVerificationBadges verifications={verifications} className='mt-5 sm:mt-6' />

                    {stats.length > 0 && (
                        <SellerStats stats={stats} className='mt-5 border-t border-ink-900/15 pt-5 sm:mt-6 sm:pt-6' />
                    )}
                </div>

                <div className='flex w-full flex-col gap-3 sm:flex-row lg:w-auto lg:shrink-0 lg:flex-col lg:pl-2'>
                    {telegramHref ? (
                        <a
                            href={telegramHref}
                            target='_blank'
                            rel='noopener noreferrer'
                            onClick={onTelegramClick}
                            className='type-label mta-focus mta-scroll-safe inline-flex h-12 w-full items-center justify-center gap-2 rounded-mta-sm bg-brand-red-ink px-6 text-[11px] text-white transition-colors duration-[var(--dur-fast)] ease-[var(--ease-brand)] hover:bg-brand-red-deep sm:w-auto lg:w-full'
                        >
                            <TelegramGlyph className='size-4' />
                            Message on Telegram
                        </a>
                    ) : (
                        <p className='mta-border rounded-mta-sm bg-surface-muted px-5 py-3 text-xs leading-relaxed text-text-muted lg:max-w-[16rem]'>
                            This seller has no public Telegram handle yet. Contact details are shown on each of their listings.
                        </p>
                    )}

                    <ShareProfileButton url={profileUrl} title={name} />
                </div>
            </div>
        </div>
    )
}
